import { useEffect } from "react";

const SECTIONS = [
  { id: "research-use-only", title: "Research Use Only" },
  { id: "intended-use", title: "Intended Use" },
  { id: "not-for-human-consumption", title: "Not for Human Consumption" },
  { id: "not-for-veterinary-use", title: "Not for Veterinary Use" },
  { id: "not-approved-as-drugs", title: "Not Approved as Drugs" },
  { id: "not-dietary-supplements", title: "Not Dietary Supplements" },
  { id: "not-cosmetics", title: "Not Cosmetics" },
  { id: "not-medical-devices", title: "Not Medical Devices" },
  { id: "no-medical-advice", title: "No Medical Advice" },
  { id: "no-therapeutic-claims", title: "No Therapeutic Claims" },
  { id: "qualified-purchasers", title: "Qualified Purchasers" },
  { id: "customer-responsibility", title: "Customer Responsibility" },
  { id: "product-information", title: "Product Information" },
  { id: "laboratory-safety", title: "Laboratory Safety" },
  { id: "compliance-with-laws", title: "Compliance With Laws" },
  { id: "order-refusal", title: "Order Refusal" },
  { id: "website-content", title: "Website Content" },
  { id: "limitation-of-responsibility", title: "Limitation of Responsibility" },
  { id: "updates-to-this-disclaimer", title: "Updates to this Disclaimer" },
  { id: "contact-information", title: "Contact Information" },
];

export default function ResearchUseDisclaimer() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Research Use Only Disclaimer | True Impact Peptides";
    let meta = document.querySelector('meta[name="description"]');
    const prevDescription = meta?.getAttribute("content");
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "Read the official Research Use Only Disclaimer for True Impact Peptides, including product use restrictions, legal notices, and important customer responsibilities.");
    return () => {
      document.title = prevTitle;
      if (meta && prevDescription !== undefined) meta.setAttribute("content", prevDescription || "");
    };
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 print:py-4" data-testid="research-use-disclaimer-page">
      <div className="mb-10 print:mb-4">
        <span className="text-xs tracking-[0.2em] uppercase font-semibold text-brandblue-600 print:hidden">Legal</span>
        <h1 className="mt-3 text-4xl sm:text-5xl tracking-tighter font-medium text-slate-900 font-display print:text-3xl">Research Use Only Disclaimer</h1>
        <p className="mt-3 text-sm text-slate-500">True Impact Peptides &middot; Last Updated: August 2026</p>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-12">
        {/* Sticky table of contents */}
        <nav className="hidden lg:block print:hidden" aria-label="Table of contents">
          <div className="sticky top-24 border border-slate-200 rounded-xl p-5 max-h-[70vh] overflow-y-auto scrollbar-thin">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">On This Page</p>
            <ul className="space-y-2 text-sm">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-slate-600 hover:text-brandblue-600 transition-colors" data-testid={`ruo-toc-${s.id}`}>
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <div className="text-sm text-slate-600 leading-relaxed space-y-5">
          <p>True Impact Peptides is committed to supplying high-quality research compounds intended exclusively for lawful laboratory research purposes.</p>
          <p>All products sold by True Impact Peptides are designated Research Use Only ("RUO") unless expressly stated otherwise.</p>
          <p>By accessing this Website, purchasing products, or otherwise interacting with True Impact Peptides, you acknowledge that you have read, understood, and agree to the terms of this Research Use Only Disclaimer.</p>

          <h2 id="research-use-only" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">Research Use Only</h2>
          <p>Products offered by True Impact Peptides are intended solely for legitimate laboratory research, analytical testing, scientific investigation, and related lawful research activities.</p>

          <h2 id="intended-use" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">Intended Use</h2>
          <p>Our products are supplied exclusively for qualified research purposes and are not marketed or represented for human or veterinary use.</p>
          <p>Customers are responsible for determining whether the purchase, possession, handling, storage, transportation, and use of any product complies with all applicable laws and regulations.</p>

          <h2 id="not-for-human-consumption" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">Not for Human Consumption</h2>
          <p>All products sold by True Impact Peptides are NOT intended for human consumption.</p>
          <p>Our products are not designed, marketed, or offered for use in or on humans.</p>
          <p>Customers agree that they will not use, encourage, promote, or facilitate the use of any product for human consumption.</p>

          <h2 id="not-for-veterinary-use" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">Not for Veterinary Use</h2>
          <p>Products sold by True Impact Peptides are NOT intended for veterinary use.</p>
          <p>They are not intended for administration to animals and are not marketed for veterinary applications.</p>

          <h2 id="not-approved-as-drugs" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">Not Approved as Drugs</h2>
          <p>Unless expressly stated otherwise, products sold by True Impact Peptides:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Are not approved pharmaceutical drugs.</li>
            <li>Have not been approved by the U.S. Food and Drug Administration (FDA) for therapeutic use.</li>
            <li>Are not approved for the diagnosis, treatment, cure, or prevention of any disease.</li>
          </ul>
          <p>Nothing on this Website should be interpreted as suggesting otherwise.</p>

          <h2 id="not-dietary-supplements" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">Not Dietary Supplements</h2>
          <p>Products sold by True Impact Peptides are not dietary supplements. They are not intended to supplement the diet and should not be marketed, labeled, or represented as nutritional products.</p>

          <h2 id="not-cosmetics" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">Not Cosmetics</h2>
          <p>Our products are not cosmetics as defined by applicable law and are not intended for cosmetic use.</p>

          <h2 id="not-medical-devices" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">Not Medical Devices</h2>
          <p>Products sold by True Impact Peptides are not medical devices and should not be represented as such.</p>

          <h2 id="no-medical-advice" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">No Medical Advice</h2>
          <p>The information provided on this Website is offered solely for informational purposes regarding our products and related laboratory documentation.</p>
          <p>Nothing contained on this Website constitutes:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Medical advice</li>
            <li>Healthcare advice</li>
            <li>Pharmaceutical advice</li>
            <li>Veterinary advice</li>
            <li>Treatment recommendations</li>
            <li>Diagnostic guidance</li>
            <li>Dosage recommendations</li>
            <li>Clinical recommendations</li>
          </ul>
          <p>Customers should not rely on Website content as a substitute for professional medical, scientific, or legal advice.</p>

          <h2 id="no-therapeutic-claims" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">No Therapeutic Claims</h2>
          <p>True Impact Peptides does not make claims that any product:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Diagnoses disease</li>
            <li>Treats disease</li>
            <li>Prevents disease</li>
            <li>Mitigates disease</li>
            <li>Cures disease</li>
            <li>Produces any specific research outcome</li>
          </ul>
          <p>Any scientific references, research publications, or educational materials made available through the Website are provided for informational purposes only and should not be interpreted as claims regarding our products.</p>

          <h2 id="qualified-purchasers" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">Qualified Purchasers</h2>
          <p>By purchasing products from True Impact Peptides, customers represent and warrant that:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>They are at least twenty-one (21) years of age.</li>
            <li>They possess sufficient knowledge to understand the nature of Research Use Only products.</li>
            <li>They are purchasing products solely for lawful research purposes.</li>
            <li>They will comply with all applicable federal, state, and local laws.</li>
            <li>They understand the limitations described in this Disclaimer.</li>
          </ul>

          <h2 id="customer-responsibility" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">Customer Responsibility</h2>
          <p>Customers assume full responsibility for:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Product selection</li>
            <li>Product handling</li>
            <li>Storage</li>
            <li>Transportation</li>
            <li>Laboratory use</li>
            <li>Disposal</li>
            <li>Compliance with applicable laws</li>
            <li>Compliance with institutional policies</li>
            <li>Compliance with safety requirements</li>
          </ul>
          <p>True Impact Peptides is not responsible for misuse of products after delivery.</p>

          <h2 id="product-information" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">Product Information</h2>
          <p>Certificates of Analysis (COAs), batch information, HPLC reports, purity data, specifications, and related documentation are provided to support transparency regarding product identity and quality.</p>
          <p>Such documentation is provided for informational purposes and should not be interpreted as a guarantee of suitability for any particular research application.</p>

          <h2 id="laboratory-safety" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">Laboratory Safety</h2>
          <p>Research compounds should be handled only in accordance with appropriate laboratory safety procedures and applicable regulations.</p>
          <p>Customers are responsible for maintaining appropriate safety practices and using suitable protective equipment where applicable.</p>

          <h2 id="compliance-with-laws" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">Compliance With Laws</h2>
          <p>Customers are solely responsible for ensuring compliance with all applicable laws governing:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Purchase</li>
            <li>Possession</li>
            <li>Transportation</li>
            <li>Storage</li>
            <li>Research</li>
            <li>Import</li>
            <li>Export</li>
            <li>Distribution</li>
            <li>Disposal</li>
          </ul>
          <p>True Impact Peptides makes no representation that products offered through this Website may lawfully be purchased or possessed in every jurisdiction.</p>

          <h2 id="order-refusal" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">Order Refusal</h2>
          <p>True Impact Peptides reserves the right to refuse, cancel, or decline any order if, in our sole discretion, we believe a transaction presents legal, regulatory, compliance, fraud, or safety concerns.</p>

          <h2 id="website-content" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">Website Content</h2>
          <p>The content provided on this Website is intended solely to describe our products and provide general educational information.</p>
          <p>Website content should never be interpreted as:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Medical advice</li>
            <li>Healthcare recommendations</li>
            <li>Product endorsements for human use</li>
            <li>Treatment recommendations</li>
            <li>Clinical guidance</li>
          </ul>

          <h2 id="limitation-of-responsibility" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">Limitation of Responsibility</h2>
          <p>To the fullest extent permitted by applicable law, True Impact Peptides disclaims responsibility for any misuse, unlawful use, improper handling, or unauthorized application of products purchased from this Website.</p>
          <p>Customers assume all risks associated with their purchase and use of Research Use Only products.</p>

          <h2 id="updates-to-this-disclaimer" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">Updates to this Disclaimer</h2>
          <p>True Impact Peptides reserves the right to modify or update this Research Use Only Disclaimer at any time.</p>
          <p>The "Last Updated" date at the top of this page will indicate when revisions become effective.</p>
          <p>Continued use of the Website after changes are posted constitutes acceptance of the revised Disclaimer.</p>

          <h2 id="contact-information" className="font-display font-semibold text-slate-900 text-xl pt-4 scroll-mt-24">Contact Information</h2>
          <p>Questions regarding this Research Use Only Disclaimer may be directed to:</p>
          <p>True Impact Peptides<br />Email: info@trueimpactpeptides.com</p>

          <button
            onClick={() => window.print()}
            className="mt-8 text-sm font-semibold text-brandblue-600 hover:text-brandblue-800 transition-colors print:hidden"
            data-testid="ruo-print-btn"
          >
            Print this page
          </button>
        </div>
      </div>
    </div>
  );
}
